import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RefreshCw, AlertCircle, ArrowLeft, ChevronDown, ChevronUp, MessageSquare, Trash2, CreditCard, BarChart3, Tag } from 'lucide-react';
import ExpandableCell from './ExpandableCell';
import MobileLogCard from './MobileLogCard';
import FilterTags from './FilterTags';
import { FilterState, LogEntry, MetadataType } from './types';
import { Smartphone, Monitor } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

interface ChatMessage {
  role: string;
  content: string;
}

const ProcessLogs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [expandAllMobile, setExpandAllMobile] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRowExpanded = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const [expandedUserRows, setExpandedUserRows] = useState<Set<string>>(new Set());

  const toggleUserRowExpanded = (id: string) => {
    setExpandedUserRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const [expandedLocationRows, setExpandedLocationRows] = useState<Set<string>>(new Set());

  const toggleLocationRowExpanded = (id: string) => {
    setExpandedLocationRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const [expandAll, setExpandAll] = useState(false);
  const [textOnlyMode, setTextOnlyMode] = useState(false);

  const handleExpandAll = () => {
    const newValue = !expandAll;
    setExpandAll(newValue);
    if (newValue) {
      const allIds = new Set(logs.map(l => String(l.id)));
      setExpandedRows(allIds);
      setExpandedUserRows(new Set(allIds));
      setExpandedLocationRows(new Set(allIds));
    } else {
      setExpandedRows(new Set());
      setExpandedUserRows(new Set());
      setExpandedLocationRows(new Set());
    }
  };
  const [selectedChatHistory, setSelectedChatHistory] = useState<ChatMessage[] | null>(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState<number | null>(null);
  
  // Separate state for form inputs and active filters
  const [filterInputs, setFilterInputs] = useState<FilterState>({
    startDate: '',
    endDate: '',
    endpoint: '',
    statusCode: '',
    country: '',
    paidUser: '',
    useTurbo: '',
    source: '',
    platform: '',
    deviceId: searchParams.get('deviceId') || '',
    mode: ''
  });

  const [activeFilters, setActiveFilters] = useState<FilterState>({
    startDate: '',
    endDate: '',
    endpoint: '',
    statusCode: '',
    country: '',
    paidUser: '',
    useTurbo: '',
    source: '',
    platform: '',
    deviceId: searchParams.get('deviceId') || '',
    mode: ''
  });

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '50',
          ...(activeFilters.startDate && { startDate: activeFilters.startDate }),
          ...(activeFilters.endDate && { endDate: activeFilters.endDate }),
          ...(activeFilters.endpoint && { endpoint: activeFilters.endpoint }),
          ...(activeFilters.statusCode && { statusCode: activeFilters.statusCode }),
          ...(activeFilters.country && { country: activeFilters.country }),
          ...(activeFilters.paidUser && { paidUser: activeFilters.paidUser }),
          ...(activeFilters.useTurbo && { useTurbo: activeFilters.useTurbo }),
          ...(activeFilters.source && { source: activeFilters.source }),
          ...(activeFilters.platform && { platform: activeFilters.platform }),
          ...(activeFilters.deviceId && { deviceId: activeFilters.deviceId }),
          ...(activeFilters.mode && { mode: activeFilters.mode })
        });

        const response = await fetch(`https://api.notely.one/dashboard/process-logs?${params}`);
        if (!response.ok) throw new Error('Failed to fetch logs');
        const data = await response.json();
        setLogs(data.logs || []);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [page, activeFilters]);

  const fetchTotalCount = useCallback(async () => {
    setCountLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '1',
        ...(activeFilters.startDate && { startDate: activeFilters.startDate }),
        ...(activeFilters.endDate && { endDate: activeFilters.endDate }),
        ...(activeFilters.endpoint && { endpoint: activeFilters.endpoint }),
        ...(activeFilters.statusCode && { statusCode: activeFilters.statusCode }),
        ...(activeFilters.country && { country: activeFilters.country }),
        ...(activeFilters.paidUser && { paidUser: activeFilters.paidUser }),
        ...(activeFilters.useTurbo && { useTurbo: activeFilters.useTurbo }),
        ...(activeFilters.source && { source: activeFilters.source }),
        ...(activeFilters.platform && { platform: activeFilters.platform }),
        ...(activeFilters.deviceId && { deviceId: activeFilters.deviceId }),
        ...(activeFilters.mode && { mode: activeFilters.mode })
      });

      const response = await fetch(`https://api.notely.one/dashboard/process-logs?${params}`);
      if (!response.ok) throw new Error(`Failed to fetch total count: ${response.status} ${response.statusText}`);
      const data = await response.json();
      
      setTotalCount(data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching total count:', error);
      setTotalCount(null);
    } finally {
      setCountLoading(false);
    }
  }, [activeFilters]);

  useEffect(() => {
    fetchTotalCount();
  }, [fetchTotalCount]);

  const handleDeleteLog = async (requestId: number) => {
    if (!window.confirm('Are you sure you want to delete this log entry? This action cannot be undone.')) {
      return;
    }

    setDeletingLogId(requestId);
    try {
      const response = await fetch(`https://api.notely.one/dashboard/process-logs/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete log: ${response.status} ${response.statusText}`);
      }

      // Remove the deleted log from the current logs state
      setLogs(prevLogs => prevLogs.filter(log => log.request_id !== requestId));
      
      // Update the total count
      setTotalCount(prevCount => prevCount ? prevCount - 1 : null);
      
    } catch (error) {
      console.error('Error deleting log:', error);
      alert('Failed to delete log entry. Please try again.');
    } finally {
      setDeletingLogId(null);
    }
  };

  const handleInputChange = (key: keyof FilterState, value: string) => {
    setFilterInputs(prev => ({ ...prev, [key]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setActiveFilters(filterInputs);
      setPage(1);
    }
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveFilters(filterInputs);
    setPage(1);
  };

  const handleQuickFilter = (
    type: 'country' | 'paidUser' | 'platform' | 'mode', 
    value: string
  ) => {
    const newInputs = { ...filterInputs };
    
    switch(type) {
      case 'country':
        newInputs.country = newInputs.country === value ? '' : value;
        break;
      case 'paidUser':
        newInputs.paidUser = newInputs.paidUser === value ? '' : value;
        break;
      case 'platform':
        newInputs.platform = newInputs.platform === value ? '' : value;
        break;
      case 'mode':
        newInputs.mode = newInputs.mode === value ? '' : value;
        break;
    }
    
    setFilterInputs(newInputs);
    setActiveFilters(newInputs);
    setPage(1);
  };

  const openChatHistory = (chatHistory: string | ChatMessage[]) => {
    try {
      // Try to parse the chat history if it's a string
      const parsedHistory = typeof chatHistory === 'string' ? JSON.parse(chatHistory) : chatHistory;
      setSelectedChatHistory(parsedHistory);
      setShowChatModal(true);
    } catch (error) {
      console.error('Failed to parse chat history:', error);
      setError('Failed to parse chat history');
    }
  };

  interface ChatHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    chatHistory: ChatMessage[] | null;
  }

  const ChatHistoryModal: React.FC<ChatHistoryModalProps> = ({ isOpen, onClose, chatHistory }) => {
    const [copied, setCopied] = useState(false);
    
    if (!isOpen || !chatHistory) return null;
    
    const copyToClipboard = () => {
      if (!chatHistory) return;
      
      const formattedChat = chatHistory.map(message => 
        `${message.role === 'user' ? '👤 User' : '🤖 Assistant'}: ${message.content}`
      ).join('\n\n');
      
      navigator.clipboard.writeText(formattedChat).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(err => {
        console.error('Failed to copy: ', err);
      });
    };
    
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-50 p-4"
        onClick={onClose}
      >
        <div 
          className="bg-gray-800 rounded-lg shadow-lg max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-white">Chat History</h3>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              ✕
            </button>
          </div>
          <div className="overflow-y-auto p-4 flex-grow">
            {Array.isArray(chatHistory) ? (
              <div className="space-y-4">
                {chatHistory.map((message, index) => (
                  <div key={index} className={`p-3 rounded-lg ${
                    message.role === 'user' 
                      ? 'bg-blue-900/30 border border-blue-800' 
                      : 'bg-gray-700/50 border border-gray-600'
                  }`}>
                    <div className="font-medium text-sm mb-1">
                      {message.role === 'user' ? '👤 User' : '🤖 Assistant'}
                    </div>
                    <div className="text-gray-200 whitespace-pre-wrap">
                      {message.content}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-red-400">
                Invalid chat history format
              </div>
            )}
          </div>
          <div className="p-4 border-t border-gray-700 flex justify-end gap-2">
            <button
              onClick={copyToClipboard}
              className={`px-4 py-2 rounded-lg transition-colors ${
                copied 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
            >
              {copied ? '✓ Copied' : '📋 Copy All'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-red-500 bg-red-100 text-red-700 rounded-lg">
        <AlertCircle className="h-4 w-4 inline mr-2" />
        Error: {error}
      </div>
    );
  }

  const toggleFilters = () => setIsFiltersVisible(!isFiltersVisible);

  const toggleAllMobileCards = () => {
    setExpandAllMobile(!expandAllMobile);
  };

  const hasChatHistory = (log: LogEntry) => {
    return !!log.chat_history;
  };

  const handleDeviceIdClick = (deviceId: string) => {
    if (!deviceId) return;
    setPage(1);
    const newFilters = { ...filterInputs, deviceId };
    setFilterInputs(newFilters);
    setActiveFilters(newFilters);
    setSearchParams(prev => {
      prev.set('deviceId', deviceId);
      prev.set('page', '1');
      return prev;
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSearchParams(prev => {
      prev.set('page', newPage.toString());
      return prev;
    });
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex gap-2 flex-wrap">
        <a 
          href="/dashboard-metrics" 
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </a>
        <a 
          href="/dashboard-logs-feedback" 
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <MessageSquare className="w-5 h-5" />
          <span>Feedback Analysis</span>
        </a>
        <a 
          href="/dashboard-subscriptions" 
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <CreditCard className="w-5 h-5" />
          <span>Subscriptions</span>
        </a>
        <a
          href="/dashboard-daily-usage"
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <BarChart3 className="w-5 h-5" />
          <span>Daily Usage</span>
        </a>
        <a
          href="/dashboard-promo-codes"
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <Tag className="w-5 h-5" />
          <span>Promo Codes</span>
        </a>
      </div>
      
      <Card className="bg-gray-800">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="text-white">Process Logs</CardTitle>
            </div>
            <button 
              onClick={toggleFilters}
              className="md:hidden flex items-center gap-2 text-white bg-gray-700 px-3 py-1 rounded-lg"
            >
              {isFiltersVisible ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Filters
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <form 
            onSubmit={handleFilterSubmit} 
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 ${!isFiltersVisible ? 'hidden md:grid' : ''}`}
          >
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                value={filterInputs.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="date"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                value={filterInputs.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Endpoint
              </label>
              <input
                type="text"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                placeholder="/process-text"
                value={filterInputs.endpoint}
                onChange={(e) => handleInputChange('endpoint', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Status Code
              </label>
              <input
                type="text"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                placeholder="200"
                value={filterInputs.statusCode}
                onChange={(e) => handleInputChange('statusCode', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Country
              </label>
              <input
                type="text"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                placeholder="SE"
                value={filterInputs.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Paid User
              </label>
              <select
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                value={filterInputs.paidUser}
                onChange={(e) => handleInputChange('paidUser', e.target.value)}
                onKeyDown={handleKeyDown}
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Turbo Mode
              </label>
              <select
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                value={filterInputs.useTurbo}
                onChange={(e) => handleInputChange('useTurbo', e.target.value)}
                onKeyDown={handleKeyDown}
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Source
              </label>
              <input
                type="text"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                placeholder="Flutter-App"
                value={filterInputs.source}
                onChange={(e) => handleInputChange('source', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Platform
              </label>
              <input
                type="text"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                placeholder="iOS"
                value={filterInputs.platform}
                onChange={(e) => handleInputChange('platform', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Device ID
              </label>
              <input
                type="text"
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                placeholder="Enter device ID"
                value={filterInputs.deviceId}
                onChange={(e) => handleInputChange('deviceId', e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Mode
              </label>
              <select
                className="w-full bg-gray-700 text-white rounded-lg p-2 border border-gray-600"
                value={filterInputs.mode}
                onChange={(e) => handleInputChange('mode', e.target.value)}
                onKeyDown={handleKeyDown}
              >
                <option value="">All</option>
                <option value="chat">Chat</option>
                <option value="feedback">Feedback</option>
                <option value="summary">Summary</option>
                <option value="offline">Offline (all models)</option>
              </select>
            </div>
            <div className="lg:col-span-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setFilterInputs({
                    startDate: '',
                    endDate: '',
                    endpoint: '',
                    statusCode: '',
                    country: '',
                    paidUser: '',
                    useTurbo: '',
                    source: '',
                    platform: '',
                    deviceId: '',
                    mode: ''
                  });
                  setActiveFilters({
                    startDate: '',
                    endDate: '',
                    endpoint: '',
                    statusCode: '',
                    country: '',
                    paidUser: '',
                    useTurbo: '',
                    source: '',
                    platform: '',
                    deviceId: '',
                    mode: ''
                  });
                  setPage(1);
                  setSearchParams(prev => {
                    prev.delete('deviceId');
                    prev.set('page', '1');
                    return prev;
                  });
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Reset Filters
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Apply Filters
              </button>
            </div>                  {/* Filters Form */}
          <FilterTags 
            filterInputs={filterInputs}
            onFilterChange={handleQuickFilter}
          />

          </form>

          {/* Total Count Display */}
          <div className="mb-4 p-3 bg-gray-700/30 rounded-lg border border-gray-600">
            <div className="flex items-center justify-between gap-2">
              {countLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                  <span className="text-gray-300">Loading total count...</span>
                </>
              ) : totalCount !== null ? (
                <span className="text-white font-medium">
                  {(() => {
                    const formattedCount = totalCount.toLocaleString();
                    const activeFiltersList = [];
                    
                    if (activeFilters.country) activeFiltersList.push(`from ${activeFilters.country}`);
                    if (activeFilters.paidUser === 'true') activeFiltersList.push('paid users');
                    if (activeFilters.paidUser === 'false') activeFiltersList.push('free users');
                    if (activeFilters.useTurbo === 'true') activeFiltersList.push('turbo enabled');
                    if (activeFilters.useTurbo === 'false') activeFiltersList.push('turbo disabled');
                    if (activeFilters.platform) activeFiltersList.push(`on ${activeFilters.platform}`);
                    if (activeFilters.mode) activeFiltersList.push(`in ${activeFilters.mode} mode`);
                    if (activeFilters.source) activeFiltersList.push(`from ${activeFilters.source}`);
                    if (activeFilters.deviceId) activeFiltersList.push(`with device ID: ${activeFilters.deviceId}`);
                    if (activeFilters.endpoint) activeFiltersList.push(`endpoint: ${activeFilters.endpoint}`);
                    if (activeFilters.statusCode) activeFiltersList.push(`status: ${activeFilters.statusCode}`);
                    if (activeFilters.startDate || activeFilters.endDate) {
                      const dateRange = [];
                      if (activeFilters.startDate) dateRange.push(`from ${activeFilters.startDate}`);
                      if (activeFilters.endDate) dateRange.push(`to ${activeFilters.endDate}`);
                      activeFiltersList.push(dateRange.join(' '));
                    }
                    
                    const filterText = activeFiltersList.length > 0 
                      ? ` ${activeFiltersList.join(', ')}` 
                      : '';
                    
                    return `Showing ${formattedCount} total records${filterText}`;
                  })()}
                </span>
              ) : (
                <span className="text-gray-400">Unable to load total count</span>
              )}
              <button
                onClick={handleExpandAll}
                className="hidden md:flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors ml-auto"
              >
                {expandAll ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {expandAll ? 'Collapse All' : 'Expand All'}
              </button>
              <button
                onClick={() => setTextOnlyMode(v => !v)}
                className="hidden md:flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition-colors"
              >
                {textOnlyMode ? 'Show All' : 'Text Only'}
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {/* Desktop View */}
            <table className="hidden md:table w-full text-left text-gray-300">
              <thead className="bg-gray-700/50 text-gray-400 text-sm">
                <tr>
                  <th className={textOnlyMode ? 'hidden' : 'px-4 py-3'}>Time</th>
                  <th className={textOnlyMode ? 'hidden' : 'px-4 py-3'}>Request ID</th>
                  <th className={textOnlyMode ? 'hidden' : 'px-4 py-3'}>Device Info</th>
                  <th className="px-4 py-3">Text</th>
                  <th className={textOnlyMode ? 'hidden' : 'px-4 py-3'}>Output</th>
                  <th className={textOnlyMode ? 'hidden' : 'px-4 py-3'}>Location</th>
                  <th className={textOnlyMode ? 'hidden' : 'px-4 py-3'}>User</th>
                  <th className={textOnlyMode ? 'hidden' : 'px-4 py-3'}>Turbo</th>
                  <th className={textOnlyMode ? 'hidden' : 'px-4 py-3'}>Chat History</th>
                  <th className={textOnlyMode ? 'hidden' : 'px-4 py-3'}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {logs.map((log) => {
                  let metadata: MetadataType = { headers: {}, cf: {} };
                  try {
                    if (log.metadata) {
                      metadata = JSON.parse(log.metadata) as MetadataType;
                    }
                  } catch (e) {
                    console.error('Failed to parse metadata:', e);
                  }

                  const decodeUTF8 = (str: string) => {
                    try {
                      return decodeURIComponent(escape(str));
                    } catch (e) {
                      return str;
                    }
                  };
                
                  const formatLocation = () => {
                    const parts = [
                      metadata?.headers?.country,
                      metadata?.headers?.city,
                      metadata?.headers?.timezone
                    ].filter(Boolean).map(str => str ? decodeUTF8(str as string) : '');
                    
                    return parts.join(', ') || 'N/A';
                  };

                  const formatDeviceInfo = () => {
                    const brand = metadata?.headers?.deviceBrand || '';
                    const model = metadata?.headers?.deviceModel || '';
                    return `${brand} ${model}`.trim() || metadata?.headers?.platform || 'N/A';
                  };

                  return (
                    <React.Fragment key={log.id}>
                    <tr className="hover:bg-gray-700/30 transition-colors group">
                      <td className={textOnlyMode ? 'hidden' : 'p-4 text-sm text-gray-400'}>
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className={textOnlyMode ? 'hidden' : 'p-4'}>
                        <div className="bg-blue-500/10 px-2 py-0.5 rounded text-blue-400 text-sm inline-block">
                          #{log.request_id}
                        </div>
                      </td>
                      <td className={textOnlyMode ? 'hidden' : 'p-4'}>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              {(metadata?.headers?.platform === 'macOS' || formatDeviceInfo().includes('macOS')) ? (
                                <Monitor className="w-4 h-4 text-blue-400" />
                              ) : (
                                <Smartphone className="w-4 h-4 text-blue-400" />
                              )}
                              <span className="text-gray-300">{formatDeviceInfo()}</span>
                            </div>
                            <button
                              onClick={() => toggleRowExpanded(String(log.id))}
                              className="text-gray-500 hover:text-gray-300 transition-colors"
                              title="Show device details"
                            >
                              {expandedRows.has(String(log.id))
                                ? <ChevronUp className="w-3.5 h-3.5" />
                                : <ChevronDown className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                          <div className="text-xs text-gray-400">
                            {metadata?.headers?.androidVersion}
                          </div>
                          <div className="text-xs text-gray-400">
                            v{metadata?.headers?.appVersion || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <ExpandableCell content={log.text} forceExpand={expandAll} />
                      </td>
                      <td className={textOnlyMode ? 'hidden' : 'p-4'}>
                      <ExpandableCell content={log.output_text} cost={log.cost} forceExpand={expandAll} />
                      </td>
                      <td className={textOnlyMode ? 'hidden' : 'p-4'}>
                        <div className="flex items-center gap-1 text-purple-400">
                          <span className="font-medium">{metadata?.headers?.country || 'N/A'}</span>
                          <button
                            onClick={() => toggleLocationRowExpanded(String(log.id))}
                            className="text-gray-500 hover:text-gray-300 transition-colors"
                            title="Show full location"
                          >
                            {expandedLocationRows.has(String(log.id))
                              ? <ChevronUp className="w-3.5 h-3.5" />
                              : <ChevronDown className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        {expandedLocationRows.has(String(log.id)) && (
                          <div className="mt-1 text-xs text-gray-400 space-y-0.5">
                            {metadata?.headers?.city && (
                              <div>{decodeUTF8(metadata.headers.city as string)}</div>
                            )}
                            {metadata?.headers?.timezone && (
                              <div className="text-gray-500">{decodeUTF8(metadata.headers.timezone as string)}</div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className={textOnlyMode ? 'hidden' : 'p-4'}>
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className={`text-sm font-medium ${
                              metadata?.headers?.paidUser === 'true' ? 'text-emerald-400' : 'text-gray-400'
                            }`}>
                              {metadata?.headers?.paidUser === 'true' ? 'Paid' : 'Free'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{metadata?.headers?.realIp}</div>
                          </div>
                          <button
                            onClick={() => toggleUserRowExpanded(String(log.id))}
                            className="text-gray-500 hover:text-gray-300 transition-colors"
                            title="Show source, mode & model info"
                          >
                            {expandedUserRows.has(String(log.id))
                              ? <ChevronUp className="w-3.5 h-3.5" />
                              : <ChevronDown className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                      <td className={textOnlyMode ? 'hidden' : 'p-4'}>
                        <div className={`text-sm font-medium ${
                          metadata?.headers?.useTurbo === 'true'
                            ? 'text-blue-400'
                            : 'text-gray-400'
                        }`}>
                          {metadata?.headers?.useTurbo === 'true' ? 'Yes' : 'No'}
                        </div>
                      </td>
                      <td className={textOnlyMode ? 'hidden' : 'p-4'}>
                        {hasChatHistory(log) ? (
                          <button
                            onClick={() => openChatHistory(log.chat_history as string)}
                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            <MessageSquare className="w-4 h-4" />
                            <span>View</span>
                          </button>
                        ) : (
                          <span className="text-gray-500">No history</span>
                        )}
                      </td>
                      <td className={textOnlyMode ? 'hidden' : 'p-4'}>
                        <button
                          onClick={() => handleDeleteLog(log.request_id)}
                          disabled={deletingLogId === log.request_id}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-600/20 rounded text-red-400 hover:text-red-300 disabled:opacity-50"
                          title="Delete log entry"
                        >
                          {deletingLogId === log.request_id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                    </tr>
                    {!textOnlyMode && expandedRows.has(String(log.id)) && (
                      <tr className="bg-gray-800/50 border-t border-gray-700/50">
                        <td colSpan={10} className="px-6 py-3">
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-300">
                            <span><span className="text-gray-500">SDK:</span> {metadata?.headers?.sdkVersion || 'N/A'}</span>
                            <span><span className="text-gray-500">System:</span> {metadata?.headers?.systemName || 'N/A'}</span>
                            <span><span className="text-gray-500">System Version:</span> {metadata?.headers?.systemVersion || 'N/A'}</span>
                            <span><span className="text-gray-500">Build:</span> {metadata?.headers?.appBuild || 'N/A'}</span>
                            <span><span className="text-gray-500">Architecture:</span> {metadata?.headers?.architecture || 'N/A'}</span>
                            <span
                              className="cursor-pointer text-blue-400 hover:text-blue-300"
                              onClick={() => handleDeviceIdClick(metadata?.headers?.deviceId || '')}
                            >
                              <span className="text-gray-500">Device ID:</span> {metadata?.headers?.deviceId || 'N/A'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {!textOnlyMode && expandedUserRows.has(String(log.id)) && (
                      <tr className="bg-gray-800/50 border-t border-gray-700/50">
                        <td colSpan={10} className="px-6 py-3">
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-300">
                            <span>
                              <span className="text-gray-500">Source:</span>{' '}
                              <span className="text-emerald-400">{metadata?.headers?.source || 'N/A'}</span>
                            </span>
                            <span>
                              <span className="text-gray-500">Mode:</span>{' '}
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                metadata?.headers?.mode === 'chat' ? 'bg-blue-500/20 text-blue-400' :
                                metadata?.headers?.mode === 'feedback' ? 'bg-yellow-500/20 text-yellow-400' :
                                metadata?.headers?.mode === 'summary' ? 'bg-green-500/20 text-green-400' :
                                metadata?.headers?.mode?.startsWith('offline') ? 'bg-purple-500/20 text-purple-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>{metadata?.headers?.mode || 'unknown'}</span>
                            </span>
                            {metadata?.headers?.transcriptionService && (
                              <span><span className="text-gray-500">Service:</span> {metadata.headers.transcriptionService}</span>
                            )}
                            {metadata?.headers?.transcriptionModel && (
                              <span><span className="text-gray-500">Transcription Model:</span> {metadata.headers.transcriptionModel}</span>
                            )}
                            {(metadata?.modelUsed || metadata?.headers?.modelUsed) && (
                              <span><span className="text-gray-500">Processing Model:</span> {metadata?.modelUsed || metadata?.headers?.modelUsed}</span>
                            )}
                            {metadata?.inputType && (
                              <span>
                                <span className="text-gray-500">Input:</span>{' '}
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  metadata.inputType === 'microphone'  ? 'bg-purple-500/20 text-purple-400' :
                                  metadata.inputType === 'youtube'     ? 'bg-red-500/20 text-red-400' :
                                  metadata.inputType === 'file_upload' ? 'bg-orange-500/20 text-orange-400' :
                                  metadata.inputType === 'app_share'   ? 'bg-cyan-500/20 text-cyan-400' :
                                  'bg-gray-500/20 text-gray-400'
                                }`}>{metadata.inputType}</span>
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            <div className="md:hidden flex justify-between items-center mb-4">
              <button 
                onClick={toggleAllMobileCards}
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                {expandAllMobile ? 
                  <ChevronUp className="w-4 h-4" /> : 
                  <ChevronDown className="w-4 h-4" />
                }
                {expandAllMobile ? 'Collapse All' : 'Expand All'}
              </button>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-4">
              {logs.map((log) => {
                let metadata: MetadataType = { headers: {}, cf: {} };
                try {
                  if (log.metadata) {
                    metadata = JSON.parse(log.metadata) as MetadataType;
                  }
                } catch (e) {
                  console.error('Failed to parse metadata:', e);
                }

                return (
                  <MobileLogCard 
                    key={log.id} 
                    log={log} 
                    metadata={metadata}
                    isExpanded={expandAllMobile}
                    onViewChatHistory={hasChatHistory(log) ? 
                      () => openChatHistory(log.chat_history as string) : 
                      undefined}
                    onDeviceIdClick={handleDeviceIdClick}
                    onDeleteLog={handleDeleteLog}
                    isDeleting={deletingLogId === log.request_id}
                  />
                );
              })}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={() => handlePageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-gray-300">Page {page}</span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={logs.length < 50}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Chat History Modal */}
      <ChatHistoryModal 
        isOpen={showChatModal}
        onClose={() => setShowChatModal(false)}
        chatHistory={selectedChatHistory}
      />
    </div>
  );
};

export default ProcessLogs;